<script lang="ts">
  import { ChevronDown, ChevronUp, Calendar } from "lucide-svelte"
  import { clickOutside } from "$app/actions"
  import Autocomplete from "./Autocomplete.svelte"
  import Expected from "$app/Pages/Plans/Goals/Filters/Expected.svelte"
  import { appStore } from "$app/store.svelte"

  let { onsearch } = $props()

  let dates = appStore.dates
  let expectedFilter: any = $state({ value: appStore.params?.date_filter })
  let auto: Autocomplete

  let showFilters = $state(false)

  let inputElem = $state<HTMLInputElement | undefined>()
  let inputVal = $state(appStore.params?.input || "")
  let filters: any[] = $state([])
  let prevFilters: any[] = $state([])
  let visibleFilters: any[] = $state([])
  let userTab: "Sender" | "Receiver" | "Sender/Receiver" = $state("Receiver")

  function onKey(evt: KeyboardEvent): void {
    const prevVal = inputVal
    switch (evt.key) {
      case "Enter":
        auto.selectIndex()
        break
      case "Backspace":
        setTimeout(() => {
          if (prevVal === inputVal) {
            filters.pop()
            filters = filters
          }
        }, 10)
        break
      case "ArrowDown":
        auto.moveDown()
        break
      case "ArrowUp":
        auto.moveUp()
        break
    }
  }

  export function getDateFilter(): any {
    if (typeof expectedFilter?.value === "object") {
      let data: any = {}

      if (expectedFilter.value?.date_start) {
        const ds = expectedFilter.value.date_start
        data["date_start"] = ds
      }
      if (expectedFilter.value?.date_end) {
        const de = expectedFilter.value.date_end
        data["date_end"] = de
      }

      return { date_filter: "custom-range", ...data }
    } else {
      return { date_filter: expectedFilter.value }
    }
  }

  function getUserFilters(): any {
    const ids = filters.map((v) => v.id)

    switch (userTab) {
      case "Sender":
        return { sender_ids: ids }
      case "Receiver":
        return { recipient_ids: ids }
      case "Sender/Receiver":
        return { sender_ids: ids, recipient_ids: ids }
      default:
        return {}
    }
  }

  let prevData: any = {}
  function search(): void {
    prevFilters = [...filters]

    showFilters = false
    const data = {
      ...getDateFilter(),
      ...getUserFilters()
    }
    if (JSON.stringify(prevData) !== JSON.stringify(data)) {
      onsearch(data)
      prevData = { ...data }
    }
  }

  function onFocus(): void {
    showFilters = true
    inputElem.focus()
  }

  function removeFilter(item: any) {
    const idx = filters.findIndex(
      (flt) => flt.value === item.value && flt.type === item.type
    )
    if (idx > -1) {
      filters.splice(idx, 1)
      filters = filters
    }
    auto.resetIndex()
    inputElem.focus()
  }

  $effect(() => {
    visibleFilters = filters
  })
</script>

<div
  class="relative recognitions-filter-cont"
  data-name="recognitions-filter-cont"
  use:clickOutside={() => {
    if (showFilters) {
      showFilters = false
      search()
    }
  }}
>
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <div
    class="bg-white rounded-md border border-gray-300 py-3 pl-4 pr-8 relative cursor search-cont flex items-center"
    onclick={onFocus}
    role="button"
    tabindex="0"
  >
    <button
      class="mr-2 cursor-pointer"
      data-name="date"
      onclick={(e) => {
        e.stopPropagation()
        showFilters = true
      }}
    >
      <Calendar class="inline text-gray-500 mr-1" />
      <span class="italic">{expectedFilter?.label}</span>
    </button>

    {#if userTab}
      <span class="user-tab font-semibold text-gray-600 mr-2">
        {userTab}
      </span>
    {/if}

    {#each filters as filt, idx (idx)}
      <span class="filter-tag flex items-center">
        {filt.name}
        <button
          class="filter-remove text-gray-100 border-gray-400 ml-1"
          onclick={() => removeFilter(filt)}
        >
          x
        </button>
      </span>
    {/each}

    <input
      type="text"
      class="border-none focus:ring-transparent focus:outline-none"
      placeholder="Search..."
      style="order:1000000000"
      bind:this={inputElem}
      bind:value={inputVal}
      onkeydown={onKey}
    />

    <button
      data-name="close-search-button"
      class="absolute right-1 top-2 p-2 z-10 transition-color"
      title={showFilters ? "Close and Search" : ""}
      onclick={(e) => {
        e.stopPropagation()
        if (showFilters) {
          search()
        } else {
          showFilters = !showFilters
        }
      }}
    >
      {#if showFilters}
        <ChevronUp size="1rem" />
      {:else}
        <ChevronDown size="1rem" />
      {/if}
    </button>
  </div>

  <div
    class="absolute bg-white w-full shadow-md z-10"
    class:hidden={!showFilters}
  >
    <div class="px-4">
      <Expected
        {dates}
        expected_at={expectedFilter}
        onselected={(data) => {
          expectedFilter = data
        }}
        rangeOptions={[
          "Current Quarter",
          "Current Year",
          "Previous Quarter",
          "Previous Year",
          "All Time"
        ]}
      />
    </div>

    <div>
      <Autocomplete
        {filters}
        bind:this={auto}
        filter={inputVal}
        onaddfilter={(filter) => (filters = [...filters, filter])}
        onselected={(tab) => {
          userTab = tab
          inputElem.focus()
        }}
      />
    </div>

    <div class="py-2 px-4 grid grid-cols-2 bg-gray-50">
      <div>
        {#if filters.length}
          <button
            class="text-blue-500 px-6 py-2 font-semibold"
            onclick={() => {
              filters = []
              inputElem.focus()
            }}
          >
            Clear All Filters
          </button>
        {/if}
      </div>
      <div class="text-right">
        <button
          data-name="recognitions-search-button"
          class="btn success px-6 text-lg font-semibold"
          onclick={() => search()}
        >
          Search
        </button>
      </div>
    </div>
  </div>
</div>

<style lang="css">
  .recognitions-filter-cont {
    z-index: 2;
  }
  .search-cont {
    display: flex;
    flex-wrap: wrap;
  }
  .filter-tag {
    border-radius: 4px;
    color: white;
    background-color: #475569;
    padding: 0px 0.3rem;
    height: 1.6rem;
    line-height: 2;
    margin-top: 5px;
    margin-right: 0.2rem;
  }
  .filter-remove {
    cursor: pointer;
    width: 1.2rem;
    height: 1.2rem;
    font-weight: bold;
    font-size: 1rem;
    line-height: 0.9;
    text-align: center;
    display: inline-block;
  }
  .transition-color {
    transition: all 300ms;
  }
</style>
